import { useMemo, useState } from 'react'

import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { partition } from 'lodash'
import { Eye, EyeOff, GripVertical, Lock, Pin } from 'lucide-react'
import { Button, DefaultTooltip, cn, Separator } from 'twyne-new.ui'

import InputSearchList from '@/components/InputSearchList'
import { DndSortableList, removeScaleY, useSortableDndListItem } from '@Components/SortableDnd'

const SECTION_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement]
const DRAG_ACTIVATION = { distance: 4 }

export function summarizeColumns(items = []) {
  return {
    total: items.length,
    visible: items.filter(({ checked }) => checked).length,
    pinned: items.filter(({ checked, pinned }) => checked && pinned).length,
  }
}

export function ColumnsSummary({ items, className }) {
  const { total, visible, pinned } = summarizeColumns(items)

  return <span className={cn('text-sm font-normal twy-text-base', className)}>
    {visible} of {total} visible · {pinned} pinned left
  </span>
}

export default function ColumnsConfigPanel({ items, header, contentClassName, onToggleChecked, onTogglePinned, onReorder, onSetChecked }) {
  const [term, setTerm] = useState('')

  const filtered = useMemo(() => {
    const search = term.trim().toLowerCase()
    return search ? items.filter(item => searchText(item).toLowerCase().includes(search)) : items
  }, [items, term])

  const [visible, hidden] = partition(filtered, 'checked')
  const [pinned, scrolling] = partition(visible, 'pinned')
  const hideable = scrolling.filter(item => !item.alwaysPin).map(({ key }) => key)
  const showable = hidden.map(({ key }) => key)

  return <div className='flex flex-col gap-4'>
    {header && <div className={contentClassName}>{header}</div>}
    <div className={contentClassName}>
      <InputSearchList placeholder="Search columns" value={term} onChange={setTerm} className='w-full' />
    </div>
    <Separator className='m-0!' />
    <Section
      className={contentClassName}
      title='Pinned left'
      icon={<Pin size={14} className='fill-current twy-text-accent' />}
      aside={<span className='text-xs twy-text-base-neutral'>they don&apos;t scroll</span>}
      items={pinned}
      onReorder={onReorder}
      onTogglePinned={onTogglePinned}
      onToggleChecked={onToggleChecked}
    />
    <Section
      className={contentClassName}
      title='Visible'
      aside={onSetChecked && hideable.length > 0 && <SectionButton onClick={() => onSetChecked(hideable, false)}>Hide all</SectionButton>}
      items={scrolling}
      onReorder={onReorder}
      onTogglePinned={onTogglePinned}
      onToggleChecked={onToggleChecked}
    />
    <Section
      className={contentClassName}
      title='Hidden'
      aside={onSetChecked && showable.length > 0 && <SectionButton onClick={() => onSetChecked(showable, true)}>Show all</SectionButton>}
      items={hidden}
      hidden
      onReorder={onReorder}
      onTogglePinned={onTogglePinned}
      onToggleChecked={onToggleChecked}
    />
  </div>
}

function searchText({ searchText: text, label }) {
  if (typeof text === 'string') {
    return text
  }

  return typeof label === 'string' ? label : ''
}

function SectionButton({ children, onClick }) {
  return <Button variant='ghost' color='accent' size='sm' className='h-6 px-2' onClick={onClick}>
    {children}
  </Button>
}

function Section({ className, title, icon, aside, items, hidden = false, onReorder, onTogglePinned, onToggleChecked }) {
  if (!items.length) {
    return null
  }

  return <div className={cn('flex flex-col gap-2', className)}>
    <div className='flex items-center justify-between min-h-6'>
      <span className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wide twy-text-base-accent'>
        {icon}
        {title}
      </span>
      {aside}
    </div>
    <ColumnList items={items} hidden={hidden} onReorder={onReorder} onTogglePinned={onTogglePinned} onToggleChecked={onToggleChecked} />
  </div>
}

function ColumnList({ items, hidden, onReorder, onTogglePinned, onToggleChecked }) {
  return <DndSortableList
    items={items}
    itemIds={items.map(({ key }) => key)}
    modifiers={SECTION_MODIFIERS}
    activationConstraint={DRAG_ACTIVATION}
    onSortEnd={nextItems => onReorder(items, nextItems)}
  >
    <div className='flex flex-col gap-1'>
      {items.map(item => <ColumnItem
        key={item.key}
        item={item}
        hidden={hidden}
        onTogglePinned={() => onTogglePinned(item.key)}
        onToggleChecked={() => onToggleChecked(item.key)}
      />)}
    </div>
  </DndSortableList>
}

function ColumnItem({ item, hidden, onTogglePinned, onToggleChecked }) {
  const { key, label, pinned, alwaysPin = false, allowReorder = true } = item
  const locked = !allowReorder
  const draggable = !hidden && !locked
  const { setNodeRef, style, attributes, listeners } = useSortableDndListItem(key, { disabled: !draggable })

  return <div
    ref={setNodeRef}
    style={removeScaleY(style)}
    {...draggable ? { ...attributes, ...listeners } : {}}
    className={cn(
      'group flex items-center gap-3 rounded-md px-3 py-2 transition-colors focus:outline-none',
      draggable && 'cursor-grab',
      pinned && !hidden && 'twy-subtle-neutral',
      !pinned && !hidden && 'twy-subtle-accent-hover',
      hidden && 'opacity-50!',
    )}
  >
    <DragHandle hidden={hidden} locked={locked} />
    <span className='flex-1 truncate'>{label}</span>
    <div className='flex items-center gap-3'>
      {!hidden && <PinToggle pinned={pinned} alwaysPin={alwaysPin} onClick={onTogglePinned} />}
      <VisibilityToggle hidden={hidden} alwaysPin={alwaysPin} onClick={onToggleChecked} />
    </div>
  </div>
}

function DragHandle({ hidden, locked }) {
  if (locked) {
    return <DefaultTooltip content='This column cannot be reordered' side='right' triggerProps={{ asChild: true }}>
      <span className='opacity-60 twy-text-base-neutral'>
        <Lock size={14} />
      </span>
    </DefaultTooltip>
  }

  if (hidden) {
    return <span className='cursor-not-allowed twy-text-base-neutral'>
      <GripVertical size={16} />
    </span>
  }

  return <span className='twy-text-base-neutral group-hover:twy-text-base-accent'>
    <GripVertical size={16} />
  </span>
}

function PinToggle({ pinned, alwaysPin, onClick }) {
  if (alwaysPin) {
    return <DefaultTooltip content='Always pinned' side='left' triggerProps={{ asChild: true }}>
      <Pin size={16} className='cursor-default fill-current twy-text-accent opacity-70' />
    </DefaultTooltip>
  }

  return <DefaultTooltip content={pinned ? 'Unpin column' : 'Pin column'} side='bottom' triggerProps={{ asChild: true }}>
    <Pin
      size={16}
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-all',
        pinned ? 'fill-current twy-text-accent' : 'twy-text-base-neutral hover:twy-text-accent',
      )}
    />
  </DefaultTooltip>
}

function VisibilityToggle({ hidden, alwaysPin, onClick }) {
  if (alwaysPin) {
    return <DefaultTooltip content='Always visible' side='left' triggerProps={{ asChild: true }}>
      <Eye size={16} className='cursor-default twy-text-base-neutral opacity-40' />
    </DefaultTooltip>
  }

  const Icon = hidden ? EyeOff : Eye

  return <DefaultTooltip content={hidden ? 'Show column' : 'Hide column'} side='bottom' triggerProps={{ asChild: true }}>
    <Icon
      size={16}
      onClick={onClick}
      className='cursor-pointer transition-colors twy-text-base-neutral hover:twy-text-base-accent'
    />
  </DefaultTooltip>
}
